import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ManagerSidebar from "../components/ManagerSidebar";

const IMAGES = [
  "/images/admin-bg-1.png",
  "/images/admin-bg-2.png",
  "/images/admin-bg-3.jpg",
  "/images/admin-bg-4.png",
  "/images/admin-bg-5.png",
];

function ManagerLayout({ children }) {
  const [currentImage, setCurrentImage] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem("sidebar_collapsed") === "true";
  });

  useEffect(() => {
    const handleToggle = (e) => {
      setCollapsed(e.detail.collapsed);
    };
    window.addEventListener("sidebar_toggle", handleToggle);
    return () => window.removeEventListener("sidebar_toggle", handleToggle);
  }, []);

  useEffect(() => {
    // Preload background images
    IMAGES.forEach((src) => {
      const img = new Image();
      img.src = src;
    });

    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % IMAGES.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 1024) setSidebarOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <div className="min-h-screen relative overflow-hidden bg-slate-950">
      {/* Background Slideshow (Seamless GPU-accelerated Crossfade) */}
      <div className="absolute inset-0 pointer-events-none">
        {IMAGES.map((img, index) => (
          <div
            key={img}
            className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-1000 ease-in-out"
            style={{
              backgroundImage: `url(${img})`,
              opacity: currentImage === index ? 1 : 0,
              zIndex: currentImage === index ? 1 : 0,
            }}
          />
        ))}
      </div>

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900/40 via-blue-900/30 to-slate-900/40 z-[2]" />

      {/* Decorative Orbs */}
      <div className="absolute top-0 right-0 w-[400px] h-[400px] lg:w-[600px] lg:h-[600px] bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-full blur-3xl animate-pulse" />
      <div
        className="absolute bottom-0 left-0 w-[300px] h-[300px] lg:w-[500px] lg:h-[500px] bg-gradient-to-tr from-cyan-500/10 to-blue-500/10 rounded-full blur-3xl animate-pulse"
        style={{ animationDelay: "1s" }}
      />

      {/* Mobile overlay backdrop */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Page content */}
      <div className="relative z-10">
        <ManagerSidebar
          mobileOpen={sidebarOpen}
          onMobileClose={() => setSidebarOpen(false)}
        />

        <div className={`${collapsed ? "lg:ml-20" : "lg:ml-72"} min-h-screen transition-all duration-500 ease-in-out`}>
          {/* Mobile top bar */}
          <div className="lg:hidden sticky top-0 z-30 flex items-center gap-3 px-4 py-3
            bg-black/30 backdrop-blur-md border-b border-white/10">
            <button
              onClick={() => setSidebarOpen(true)}
              className="w-9 h-9 flex items-center justify-center rounded-xl
                bg-white/10 hover:bg-white/20 border border-white/20
                text-white transition-all active:scale-95"
              aria-label="Open menu"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <span className="text-white font-bold text-sm tracking-wide">Manager Portal</span>
          </div>

          <div className="p-4 sm:p-6 lg:p-8">
            <div className="w-full max-w-full">
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ManagerLayout;